/**
 * One image, uploaded or cleared (spec 42 R6).
 *
 * Reuses `useS3Upload` + `storeCatalog.requestImageUpload` — the same presign
 * path product images take, which is what makes the server-side ownership
 * check pass: it only accepts URLs under this company's storage prefix, so an
 * image pasted from anywhere else is refused on save.
 */
import { useEffect, useRef, useState } from "react";
import { ImageOff, Upload } from "lucide-react";
import { Button, toast } from "@flint/ui";
import { useS3Upload } from "../../../lib/use-s3-upload.ts";
import { trpc } from "../../../lib/trpc.ts";

const ALLOWED = ["image/jpeg", "image/png", "image/webp"] as const;
const MAX_BYTES = 5 * 1024 * 1024;
const NO_INVALIDATION: never[] = [];

export function SectionImageField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string | undefined;
  onChange: (url: string | undefined) => void;
  hint?: string;
}) {
  const [storageOff, setStorageOff] = useState(false);
  const requestUpload = trpc.storeCatalog.requestImageUpload.useMutation();
  const uploadedUrl = useRef<string | null>(null);

  const upload = useS3Upload({
    request: {
      mutateAsync: async (input) => {
        const contentType = ALLOWED.find((t) => t === input.contentType);
        if (!contentType) throw new Error("Use a JPEG, PNG or WebP image.");
        const res = await requestUpload.mutateAsync({ contentType, size: input.size });
        uploadedUrl.current = res.publicUrl;
        return { uploadUrl: res.uploadUrl, objectKey: res.objectKey };
      },
    },
    confirm: { mutateAsync: async () => ({}) },
    allowedTypes: ALLOWED,
    maxBytes: MAX_BYTES,
    invalidateQueries: NO_INVALIDATION,
    copy: { invalidType: "Use a JPEG, PNG or WebP image.", tooLarge: "Image must be under 5 MB." },
    onSuccess: () => {
      const next = uploadedUrl.current;
      uploadedUrl.current = null;
      if (next) onChange(next);
    },
  });

  // A disabled bucket is configuration, not failure: latch it once rather than
  // toasting the same error on every attempt.
  useEffect(() => {
    if (upload.status.kind !== "error") return;
    if (/storage isn't configured|uploads are disabled/i.test(upload.status.message)) {
      setStorageOff(true);
      return;
    }
    toast.error("Couldn't upload the image", { description: upload.status.message });
  }, [upload.status]);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-ink">{label}</span>
      <div className="flex items-start gap-3">
        <div className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-sm border border-line bg-raised">
          {value ? (
            <img src={value} alt="" className="size-full object-cover" />
          ) : (
            <ImageOff className="size-5 text-placeholder" aria-hidden />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <Button
              type="button"
              intent="ghost"
              size="md"
              onClick={upload.openPicker}
              disabled={upload.isBusy || storageOff}
            >
              <Upload className="size-4" aria-hidden />
              {upload.isBusy ? "Uploading…" : value ? "Replace" : "Upload"}
            </Button>
            {value ? (
              <Button type="button" intent="ghost" size="md" onClick={() => onChange(undefined)}>
                Remove
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted">
            {storageOff
              ? "Uploads are unavailable — object storage isn't configured for this environment."
              : (hint ?? "JPEG, PNG or WebP, up to 5 MB.")}
          </p>
        </div>
      </div>
      <input ref={upload.inputRef} className="hidden" {...upload.inputProps} />
    </div>
  );
}
